import * as React from 'react'
import { motion } from 'framer-motion'
import type { DeviceClass } from '@/mobile/useDeviceClass'

const EASE = [0.22, 1, 0.36, 1]

/**
 * Hosts a route's window (TransactionForm, vault/category forms, the
 * transaction list) over the mobile table.
 *
 * Phone: full-height sheet 44px below the top with a grab handle.
 * Tablet: right-docked 400px panel.
 *
 * The windows themselves are unchanged — NonModalWindow is `absolute inset-0`
 * and simply fills the frame. Phone sizing (44px fields, full-width footer
 * buttons) lives in index.css under `.pf-route-sheet`.
 *
 * The backdrop deliberately doesn't close: the forms own their close path,
 * including the "discard changes?" confirmation.
 */
export default function RouteSheet({
  device,
  children,
}: {
  device: Exclude<DeviceClass, 'desktop'>
  children: React.ReactNode
}) {
  const isPhone = device === 'phone'

  return (
    <div className="fixed inset-0 z-40">
      <motion.div
        className="absolute inset-0 bg-[hsl(var(--fill-modal-overlay))]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.16 }}
      />
      <motion.div
        data-device={device}
        className={
          isPhone
            ? 'pf-route-sheet absolute inset-x-0 bottom-0 top-11 flex flex-col overflow-hidden rounded-t-2xl bg-white shadow-[0_-10px_40px_rgba(15,23,42,.18)]'
            : 'pf-route-sheet absolute bottom-0 right-0 top-0 flex w-[400px] max-w-full flex-col overflow-hidden border-l border-primary bg-white shadow-[0_10px_40px_rgba(15,23,42,.16)]'
        }
        initial={isPhone ? { y: '100%' } : { x: '100%' }}
        animate={isPhone ? { y: 0 } : { x: 0 }}
        transition={{ duration: 0.24, ease: EASE }}
      >
        {isPhone && (
          <div className="flex shrink-0 justify-center pt-2">
            <div className="h-1 w-9 rounded-sm bg-gray-default" />
          </div>
        )}
        <div className="relative min-h-0 flex-1">{children}</div>
      </motion.div>
    </div>
  )
}
